import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { Add, Delete } from "@mui/icons-material";
import { FieldArray, Formik } from "formik";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import {
  ModalActionButtonStyles,
  ModalBaseStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";

const CHOICE_TYPES = ["radio", "checkbox", "dropdown"];

export const CANONICAL_DATE_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "DD/MM/YYYY HH:mm",
  "MM/DD/YYYY HH:mm",
  "HH:mm",
  "HH:mm:ss",
  "YYYY",
  "MM/YYYY",
];

export const CANONICAL_QUESTION_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "radio", label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
  { value: "dropdown", label: "Dropdown" },
  { value: "statement", label: "Statement" },
  { value: "slider", label: "Slider" },
];

const emptyChoice = () => ({
  label: "",
  value: "",
  position: 0,
  isOther: false,
});

export const toSnakeCase = (str: string) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

const sortChoicesByPosition = (choices: any[]) =>
  [...choices].sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
  );

type Props = {
  data?: any;
  open: boolean;
  closeModal: () => void;
  refreshPage: () => void;
};

const schema = yup.object().shape({
  label: yup.string().trim().required("Label is required"),
  varname: yup
    .string()
    .trim()
    .required("Varname is required")
    .matches(/^[a-z][a-z0-9_]*$/, "Use snake_case starting with a letter"),
  type: yup.string().required("Type is required"),
  choices: yup.array().when("type", {
    is: (type: string) => CHOICE_TYPES.includes(type),
    then: (s) =>
      s
        .of(
          yup.object().shape({
            label: yup.string().trim().required("Choice label is required"),
            value: yup
              .number()
              .typeError("Value must be a number")
              .required("Value is required"),
          }),
        )
        .min(2, "At least 2 choices are required"),
    otherwise: (s) => s.notRequired(),
  }),
  format: yup.string().when("type", {
    is: "date",
    then: (s) =>
      s
        .required("Date format is required")
        .oneOf(CANONICAL_DATE_FORMATS, "Invalid date format"),
    otherwise: (s) => s.notRequired(),
  }),
});

const CanonicalQuestionModal = ({
  data,
  open,
  closeModal,
  refreshPage,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const isEdit = !!data?.id;
  const varnameManualRef = useRef(isEdit);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const isChoiceType = CHOICE_TYPES.includes(values.type);
      const body: any = {
        label: values.label.trim(),
        varname: values.varname.trim(),
        type: values.type,
      };

      if (isChoiceType) {
        body.choices = values.choices.map((c: any, index: number) => ({
          label: String(c.label).trim(),
          value: Number(c.value),
          position: index + 1,
          isOther: !!c.isOther,
        }));
      }

      if (values.type === "date") {
        body.format = values.format;
      }

      let res: AxiosResponse;
      if (isEdit) {
        res = await http.patch(
          `/admin/registry/canonical-questions/${data.id}`,
          body,
        );
      } else {
        res = await http.post(`/admin/registry/canonical-questions`, body);
      }

      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const initialChoices =
    data?.choices?.length > 0
      ? sortChoicesByPosition(data.choices).map((c: any, index: number) => ({
          label: c.label ?? "",
          value: c.value ?? "",
          position: c.position ?? index + 1,
          isOther: !!c.isOther,
        }))
      : [emptyChoice(), emptyChoice()];

  return (
    <Modal open={open} onClose={closeModal}>
      <Box
        sx={{
          ...ModalBaseStyles,
          minHeight: "20vh",
          width: { xs: "96vw", md: "520px" },
        }}
      >
        <ModalHeader
          title={isEdit ? "Edit Canonical Question" : "Add Canonical Question"}
          onCloseClick={closeModal}
        />
        <Formik
          enableReinitialize
          initialValues={{
            label: data?.label || "",
            varname: data?.varname || "",
            type: data?.type || "text",
            format: data?.format || "MM/DD/YYYY",
            choices: initialChoices,
          }}
          validationSchema={schema}
          onSubmit={submitHandler}
        >
          {({
            handleSubmit,
            getFieldProps,
            setFieldValue,
            values,
            errors,
            touched,
          }) => (
            <form onSubmit={handleSubmit}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="cq-label">
                  Label <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  fullWidth
                  id="cq-label"
                  placeholder="Label"
                  {...getFieldProps("label")}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFieldValue("label", next);
                    if (!isEdit && !varnameManualRef.current) {
                      setFieldValue("varname", toSnakeCase(next));
                    }
                  }}
                  error={!!(touched.label && errors.label)}
                  helperText={
                    touched.label && errors.label
                      ? (errors.label as string)
                      : " "
                  }
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="cq-varname">
                  Varname <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  fullWidth
                  id="cq-varname"
                  placeholder="snake_case_name"
                  {...getFieldProps("varname")}
                  onChange={(e) => {
                    varnameManualRef.current = true;
                    setFieldValue("varname", e.target.value);
                  }}
                  error={!!(touched.varname && errors.varname)}
                  helperText={
                    touched.varname && errors.varname
                      ? (errors.varname as string)
                      : " "
                  }
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="cq-type">
                  Type <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  id="cq-type"
                  displayEmpty
                  {...getFieldProps("type")}
                  onChange={(e) => {
                    const nextType = e.target.value as string;
                    setFieldValue("type", nextType);
                    if (
                      CHOICE_TYPES.includes(nextType) &&
                      (!values.choices || values.choices.length < 2)
                    ) {
                      setFieldValue("choices", [emptyChoice(), emptyChoice()]);
                    }
                    if (nextType === "date" && !values.format) {
                      setFieldValue("format", "MM/DD/YYYY");
                    }
                  }}
                  error={!!(touched.type && errors.type)}
                >
                  {CANONICAL_QUESTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ minHeight: 20, mt: 0.5 }}
                >
                  {touched.type && errors.type ? (errors.type as string) : " "}
                </Typography>
              </FormControl>

              {values.type === "date" && (
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="cq-format">
                    Date format <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Select
                    id="cq-format"
                    displayEmpty
                    {...getFieldProps("format")}
                    error={!!(touched.format && errors.format)}
                  >
                    {CANONICAL_DATE_FORMATS.map((fmt) => (
                      <MenuItem key={fmt} value={fmt}>
                        {fmt}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ minHeight: 20, mt: 0.5 }}
                  >
                    {touched.format && errors.format
                      ? (errors.format as string)
                      : " "}
                  </Typography>
                </FormControl>
              )}

              {CHOICE_TYPES.includes(values.type) && (
                <Box sx={{ ...InputWrapper, mb: 1 }}>
                  <Typography sx={{ ...LabelStyle, mb: 1.5 }}>
                    Choices <span style={{ color: "red" }}>*</span>
                  </Typography>
                  {typeof errors.choices === "string" && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mb: 1, display: "block" }}
                    >
                      {errors.choices}
                    </Typography>
                  )}
                  <FieldArray name="choices">
                    {({ push, remove }) => (
                      <>
                        {values.choices?.map((_: any, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              alignItems: "flex-start",
                            }}
                          >
                            <FormControl sx={{ ...InputWrapper, flex: 1 }}>
                              <FormLabel sx={LabelStyle}>Label</FormLabel>
                              <TextField
                                placeholder="Choice label"
                                {...getFieldProps(`choices.${index}.label`)}
                                error={
                                  !!(
                                    (touched.choices as any)?.[index]?.label &&
                                    (errors.choices as any)?.[index]?.label
                                  )
                                }
                                helperText={
                                  (touched.choices as any)?.[index]?.label &&
                                  (errors.choices as any)?.[index]?.label
                                    ? (errors.choices as any)[index].label
                                    : " "
                                }
                              />
                            </FormControl>
                            <FormControl sx={{ width: 120 }}>
                              <FormLabel sx={LabelStyle}>Value</FormLabel>
                              <TextField
                                type="number"
                                placeholder="Value"
                                {...getFieldProps(`choices.${index}.value`)}
                                error={
                                  !!(
                                    (touched.choices as any)?.[index]?.value &&
                                    (errors.choices as any)?.[index]?.value
                                  )
                                }
                                helperText={
                                  (touched.choices as any)?.[index]?.value &&
                                  (errors.choices as any)?.[index]?.value
                                    ? (errors.choices as any)[index].value
                                    : " "
                                }
                              />
                            </FormControl>
                            <IconButton
                              color="error"
                              sx={{ mt: 3.5 }}
                              disabled={values.choices.length <= 2}
                              onClick={() => remove(index)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() =>
                            push({
                              ...emptyChoice(),
                              position: values.choices.length + 1,
                            })
                          }
                          sx={{ mt: 0.5, width: "fit-content" }}
                        >
                          Add choice
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </Box>
              )}

              <Box sx={ModalActionButtonStyles}>
                {!submitLoader ? (
                  <>
                    <Button onClick={closeModal} variant="outlined">
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained">
                      Save
                    </Button>
                  </>
                ) : (
                  <CircularProgress size={25} />
                )}
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default CanonicalQuestionModal;
